import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import {
    TrendingUp, RefreshCw,
    CheckCircle, XCircle, ArrowLeftRight, History, ChevronDown, ChevronUp
} from 'lucide-react';
import API_BASE_URL from '../config/api';

const BinaryPayout = () => {
    const [loading, setLoading] = useState(true);
    const [payoutData, setPayoutData] = useState(null);
    const [error, setError] = useState(null);
    const [showHistory, setShowHistory] = useState(false);

    const fetchPayoutData = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/user/binary_payout.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setPayoutData(data.data);
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to fetch payout data');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPayoutData();
    }, [fetchPayoutData]);

    const formatCurrency = (amount) => {
        return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
    };

    const formatPV = (pv) => {
        return parseFloat(pv || 0).toFixed(2);
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
                    <XCircle className="mx-auto text-red-500 mb-2" size={40} />
                    <p className="text-red-600">{error}</p>
                    <button
                        onClick={fetchPayoutData}
                        className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-xl hover:bg-red-200"
                    >
                        Retry
                    </button>
                </div>
            </Layout>
        );
    }

    const preview = payoutData?.preview || {};
    const currentStatus = payoutData?.current_status || {};
    const history = payoutData?.history || [];

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Binary Team Payout</h1>
                        <p className="text-gray-500 text-sm mt-1">Real-time matching and performance bonus status</p>
                    </div>
                    <button
                        onClick={fetchPayoutData}
                        className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 rounded-xl hover:bg-gray-50 font-medium transition-colors border border-gray-100 shadow-sm group"
                    >
                        <RefreshCw size={18} className="group-hover:rotate-180 transition-transform duration-500" />
                        Sync Data
                    </button>
                </div>

                {/* Main Payout Card */}
                <div className="bg-white rounded-[2rem] shadow-xl border border-gray-50 overflow-hidden">
                    <div className="p-8">
                        <div className="flex flex-col lg:flex-row gap-8">
                            {/* Left Side: Summary */}
                            <div className="flex-1 space-y-6">
                                <div className="bg-indigo-600 rounded-3xl p-8 text-white relative overflow-hidden group">
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-bl-[4rem] -mr-8 -mt-8 transition-all group-hover:scale-110"></div>
                                    <TrendingUp size={48} className="absolute -right-4 -bottom-4 text-white/10" />

                                    <p className="text-indigo-100 text-sm font-medium">Potential Total Payout</p>
                                    <h2 className="text-4xl font-black mt-1 font-fredoka">
                                        {formatCurrency(preview.total_payout)}
                                    </h2>

                                    <div className="mt-6 flex flex-wrap gap-3">
                                        <div className="px-3 py-1 bg-white/20 rounded-lg text-[10px] font-bold uppercase tracking-wider backdrop-blur-sm border border-white/10">
                                            Matching: {formatCurrency(preview.matching_income)}
                                        </div>
                                        {preview.performance_bonus > 0 && (
                                            <div className="px-3 py-1 bg-yellow-400/30 rounded-lg text-[10px] font-bold uppercase tracking-wider backdrop-blur-sm border border-yellow-400/20 text-yellow-100">
                                                Bonus: {formatCurrency(preview.performance_bonus)}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-blue-50/50 rounded-2xl p-5 border border-blue-100">
                                        <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mb-1">Left Active PV</p>
                                        <p className="text-2xl font-black text-blue-700">{formatPV(preview.effective_left_pv)}</p>
                                    </div>
                                    <div className="bg-purple-50/50 rounded-2xl p-5 border border-purple-100">
                                        <p className="text-[10px] font-bold text-purple-600 uppercase tracking-widest mb-1">Right Active PV</p>
                                        <p className="text-2xl font-black text-purple-700">{formatPV(preview.effective_right_pv)}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Right Side: Breakdown & Eligibility */}
                            <div className="lg:w-80 space-y-4">
                                <div className={`rounded-3xl p-6 border-2 flex flex-col items-center text-center ${currentStatus.is_eligible ? 'bg-green-50/50 border-green-200' : 'bg-red-50/50 border-red-200'
                                    }`}>
                                    <div className={`p-3 rounded-full mb-3 ${currentStatus.is_eligible ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                                        }`}>
                                        {currentStatus.is_eligible ? <CheckCircle size={32} /> : <XCircle size={32} />}
                                    </div>
                                    <h3 className={`font-bold text-lg ${currentStatus.is_eligible ? 'text-green-700' : 'text-red-700'}`}>
                                        {currentStatus.is_eligible ? 'Qualified for Payout' : 'Not Qualified'}
                                    </h3>
                                    <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                                        {currentStatus.is_eligible
                                            ? 'Everything is set! You are eligible to receive matching income and bonuses.'
                                            : 'Requires 25 PV / 50 PV first level completion to unlock binary payouts.'}
                                    </p>
                                </div>

                                <div className="bg-gray-50 rounded-3xl p-6 border border-gray-100">
                                    <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-4">Calculation Data</h4>
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center text-xs">
                                            <span className="text-gray-500">Matching PV</span>
                                            <span className="font-bold text-gray-700">{formatPV(preview.matching_pv)} PV</span>
                                        </div>
                                        <div className="flex justify-between items-center text-xs">
                                            <span className="text-gray-500">Matching Rate</span>
                                            <span className="font-bold text-gray-700">₹40 / PV</span>
                                        </div>
                                        <div className="flex justify-between items-center text-xs">
                                            <span className="text-gray-500 italic">Uncapped Income</span>
                                            <span className="font-medium text-gray-400">{formatCurrency(preview.uncapped_matching_income)}</span>
                                        </div>
                                        <div className="pt-3 border-t border-gray-200 flex justify-between items-center text-sm">
                                            <span className="font-bold text-gray-800">Final Income</span>
                                            <span className="font-black text-indigo-600">{formatCurrency(preview.matching_income)}</span>
                                        </div>
                                        {preview.is_capped && (
                                            <p className="text-[9px] font-black text-red-500 uppercase text-center mt-2 animate-pulse">
                                                ★ Cycle Cap Applied (₹5,000)
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Carry Forward Preview */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-3xl p-6 border border-gray-100 flex items-center gap-5 shadow-sm">
                        <div className="bg-blue-100 p-3 rounded-2xl text-blue-600"><ArrowLeftRight size={24} /></div>
                        <div>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Post-Payout Carry Filter</p>
                            <div className="flex gap-4 mt-1">
                                <div><span className="text-xs font-medium text-gray-500">LEFT:</span> <span className="text-lg font-black text-blue-600">{formatPV(preview.new_left_carry_forward)}</span></div>
                                <div><span className="text-xs font-medium text-gray-500">RIGHT:</span> <span className="text-lg font-black text-purple-600">{formatPV(preview.new_right_carry_forward)}</span></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* History Section Toggle */}
                <div className="pt-4">
                    <button
                        onClick={() => setShowHistory(!showHistory)}
                        className="flex items-center justify-between w-full p-6 bg-white rounded-3xl border border-gray-100 shadow-sm hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center gap-4">
                            <div className="bg-amber-100 p-2 rounded-xl text-amber-600"><History size={20} /></div>
                            <div className="text-left">
                                <h3 className="text-sm font-black text-gray-900 uppercase tracking-tight">Payout History</h3>
                                <p className="text-[10px] text-gray-500 font-medium">View your previous cycle settlements</p>
                            </div>
                        </div>
                        {showHistory ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />}
                    </button>

                    {showHistory && (
                        <div className="mt-4 bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-xl animate-in fade-in slide-in-from-top-4 duration-500">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead className="bg-gray-50/50 text-[10px] uppercase font-black tracking-widest text-gray-400">
                                        <tr>
                                            <th className="px-8 py-5">Cycle / Date</th>
                                            <th className="px-8 py-5 text-center">Volume (L/R)</th>
                                            <th className="px-8 py-5 text-center">Settled PV</th>
                                            <th className="px-8 py-5 text-right">Amount Earned</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {history.length > 0 ? history.map((item, i) => (
                                            <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                                                <td className="px-8 py-6">
                                                    <p className="text-sm font-black text-gray-900">#{item.cycle_number}</p>
                                                    <p className="text-[10px] text-gray-400 font-bold uppercase mt-0.5">{item.payout_date}</p>
                                                </td>
                                                <td className="px-8 py-6">
                                                    <div className="flex justify-center gap-4 text-center">
                                                        <div><p className="text-[8px] opacity-30 font-black">L-VOL</p><p className="text-xs font-bold text-blue-600">{formatPV(item.effective_left_pv)}</p></div>
                                                        <div><p className="text-[8px] opacity-30 font-black">R-VOL</p><p className="text-xs font-bold text-purple-600">{formatPV(item.effective_right_pv)}</p></div>
                                                    </div>
                                                </td>
                                                <td className="px-8 py-6 text-center">
                                                    <p className="text-sm font-black text-gray-700">{formatPV(item.matching_pv)} PV</p>
                                                    <p className="text-[9px] italic text-gray-400">Rate: ₹40</p>
                                                </td>
                                                <td className="px-8 py-6 text-right">
                                                    <div className="flex flex-col items-end">
                                                        <p className="text-lg font-black text-emerald-600 font-fredoka">{formatCurrency(item.total_payout)}</p>
                                                        {parseFloat(item.performance_bonus || 0) > 0 && (
                                                            <p className="text-[9px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full uppercase mt-1">
                                                                Incl. Bonus: {formatCurrency(item.performance_bonus)}
                                                            </p>
                                                        )}
                                                        <span className="text-[9px] font-bold text-green-600 uppercase mt-1">Settled</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="4" className="px-8 py-16 text-center text-gray-400 italic font-medium uppercase tracking-widest text-xs">
                                                    No payout records found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default BinaryPayout;
